import { Link } from "react-router-dom";
import { ArrowRight, Check, Truck, ExternalLink } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { money, subPrice } from "@/lib/api";
import { MEDIA, STACKS, HOW, HOW_GROUPS, NOT_STUDIED, STORIES } from "@/data/stacks";
import { FDA } from "@/data/content";
import { PurchaseOptions } from "@/components/ProductPanel";
import { LazyVideo } from "@/components/LazyVideo";
import { ClaimNote, Warnings } from "@/components/Wave";
import { OceanPanel, OceanImage, OceanShip, OceanPrice } from "@/components/Ocean";
import { usePrice } from "@/components/Usd";
import { TypeBadge, Contains } from "@/components/ProductBadge";
import { MemberLine, BundleSave } from "@/components/ProductExtras";

export const OceanCard = ({ p }) => (
  <div data-testid={`ocean-card-${p.id}`} className="sticker bg-white p-5 flex flex-col">
    <OceanImage p={p} compact />
    <div className="mt-5"><OceanShip id={`card-${p.id}`} us={!!p.ship_countries} /></div>
    <TypeBadge p={p} className="mt-3 self-start" />
    <h3 className="mt-2 text-2xl">{p.short_name}</h3>
    {p.subtitle && <p className="mt-1 text-sm text-slate-600">{p.subtitle}</p>}
    <Contains p={p} />
    <div className="mt-4 text-sm"><OceanPrice p={p} testId={`ocean-card-price-${p.id}`} /></div>
    <div className="mt-auto pt-5"><Link to={`/shop/${p.id}`} data-testid={`ocean-view-${p.id}`} className="btn-pop bg-sunny text-navy">{p.coming_soon ? "Coming soon" : p.kind === "single" ? "View product" : "View stack"} <ArrowRight className="h-4 w-4" /></Link></div>
  </div>
);

export const UsOnly = ({ id }) => (
  <p data-testid={`us-only-${id}`} className="inline-flex items-center gap-2 rounded-full bg-foam px-3 py-1 text-xs font-semibold"><Truck className="h-4 w-4" /> US only</p>
);

const AddOn = ({ id }) => {
  const { byId, addToCart } = useStore();
  const a = byId[id];
  if (!a || !a.active || a.available < 1) return null;
  return (
    <div data-testid={`addon-${id}`} className="mt-6 sticker-sm bg-white p-5 flex flex-wrap items-center justify-between gap-4">
      <div>
        <p className="font-display text-lg">Optional add-on: {a.short_name}</p>
        <p className="text-sm text-slate-600">Sold separately, {money(a.price_usd)} one-time. <Link to={`/shop/${id}`} data-testid={`addon-view-${id}`} className="underline decoration-sunny decoration-2 underline-offset-4">See details & warnings</Link></p>
      </div>
      <button type="button" data-testid={`addon-add-${id}`} onClick={() => addToCart(id, 1)} className="btn-ghost">Add {a.short_name}</button>
    </div>
  );
};

const HowCard = ({ k, gid }) => {
  const c = HOW[k];
  return (
    <div data-testid={`how-card-${gid}-${k}`} className="sticker bg-white p-5">
      <p className="label-chip">{c.title}</p>
      <p className="mt-3 text-sm leading-relaxed text-slate-700">{c.text}</p>
      <p className="mt-3 text-xs text-slate-600">
        <a href={c.url} target="_blank" rel="noreferrer" data-testid={`how-source-${gid}-${k}`} className="inline-flex items-center gap-1 font-semibold underline decoration-sunny decoration-2 underline-offset-4">Source <ExternalLink className="h-3 w-3" /></a>
        {c.url2 && <> · <a href={c.url2} target="_blank" rel="noreferrer" data-testid={`how-source2-${gid}-${k}`} className="font-semibold underline decoration-sunny decoration-2 underline-offset-4">Source 2</a></>}
        <span className="ml-1">({c.source})</span>
      </p>
    </div>
  );
};

export const HowGroup = ({ g, showName = true, fda = true }) => (
  <div data-testid={`how-group-${g.id}`}>
    {showName && <h3 className="text-xl md:text-2xl">{g.name}</h3>}
    <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{g.cards.map((k) => <HowCard key={k} k={k} gid={g.id} />)}</div>
    {fda && <p data-testid={`how-fda-${g.id}`} className="mt-4 text-xs text-slate-600">*{FDA} {NOT_STUDIED}</p>}
  </div>
);

const Facts = ({ id }) => (
  <div data-testid={`stack-facts-${id}`} className="mt-8 rounded-3xl bg-navy text-sand p-6 space-y-6">
    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-foam">Supplement facts & ingredients · Manufactured in the USA</p>
    {STACKS[id].facts.map((f) => (
      <div key={f.name}>
        <p className="font-display text-lg">{f.name}</p>
        <p className="mt-1 text-sm text-sand/80">Suggested use: {f.use}</p>
        <ul className="mt-2 space-y-1 text-sm text-sand/90 list-disc pl-5">{f.lines.map((l) => <li key={l}>{l}</li>)}</ul>
      </div>
    ))}
  </div>
);

export const StackPanel = ({ p }) => {
  const s = STACKS[p.id];
  const g = HOW_GROUPS.find((x) => x.id === p.id);
  if (s.ocean) return <OceanPanel p={p} />;
  return (
    <>
      <section id={p.id} data-testid={`product-panel-${p.id}`} className="container-gf py-12 md:py-16 grid lg:grid-cols-[0.8fr_1.2fr] gap-10 lg:gap-16 items-start scroll-mt-28">
        <OceanImage p={p} className="lg:sticky lg:top-28 lg:aspect-square" />
        <div className="rise">
          <UsOnly id={p.id} />
          <h2 data-testid={`product-name-${p.id}`} className="mt-4 text-3xl md:text-5xl leading-[1.05]">{p.short_name}</h2>
          <p data-testid={`product-price-${p.id}`} className="mt-5 text-2xl font-semibold">{money(p.price_usd)} one-time · {money(subPrice(p))} Subscribe & save</p>
          <MemberLine p={p} className="mt-1 text-sm" />
          <BundleSave p={p} className="mt-1" />
          <p className="mt-1 text-xs text-slate-600">SKU {s.sku}</p>
          {p.kind === "stack" && <div data-testid={`stack-inside-${p.id}`} className="mt-6 sticker-sm bg-white p-5">
            <p className="font-display text-lg">What's inside</p>
            <ul className="mt-2 space-y-1 text-sm">{p.contents.map((c) => <li key={c} className="flex gap-2"><Check className="h-4 w-4 mt-0.5 shrink-0" strokeWidth={3} />{c}</li>)}</ul>
          </div>}
          <div data-testid={`stack-claims-${p.id}`} className="mt-6 sticker-sm bg-foam/60 p-5">
            <ul className="space-y-2 text-sm font-medium">{s.claims.map((c) => <li key={c}>{c}</li>)}</ul>
          </div>
          <ClaimNote testId={`claim-disclaimer-${p.id}`} className="mt-3" />
          <PurchaseOptions p={p} />
          {s.addon && <AddOn id={s.addon} />}
          <div className="mt-6"><Warnings text={<>{s.warnings.map((w) => <span key={w} className="block mt-2 first:mt-0">{w}</span>)}</>} testId={`warnings-block-${p.id}`} /></div>
          <Facts id={p.id} />
        </div>
      </section>
      <section data-testid={`stack-science-${p.id}`} className="container-gf pb-16">
        <span className="label-chip">The science</span>
        <div className={`mt-6 grid gap-8 ${s.science ? "lg:grid-cols-[280px_1fr]" : ""} items-start`}>
          {MEDIA[s.science] && <LazyVideo media={MEDIA[s.science]} caption={false} testId={`stack-science-reel-${p.id}`} className="mx-auto w-full max-w-[280px]" />}
          {g && <HowGroup g={g} showName={false} fda={false} />}
        </div>
      </section>
      <StorySection id={p.id} />
    </>
  );
};

const StorySection = ({ id }) => {
  const s = STORIES[id];
  if (!s) return null;
  return (
    <section data-testid={`stack-story-${id}`} className="bg-white/60 border-t border-navy/10">
      <div className="container-gf section max-w-3xl">
        <span className="label-chip">The science & Grandpa's story</span>
        <p className="mt-3 text-sm text-slate-600">{s.sub}</p>
        <h3 className="mt-8 text-2xl">The science</h3>
        <p data-testid={`stack-story-science-${id}`} className="mt-3 leading-relaxed text-slate-700">{s.science}</p>
        <p className="mt-6 font-semibold">Claims</p>
        <ul data-testid={`stack-story-claims-${id}`} className="mt-2 list-disc pl-5 space-y-1 text-sm">{s.claims.map((c) => <li key={c}>{c}</li>)}</ul>
        <p className="mt-6 font-semibold">Warnings</p>
        <p data-testid={`stack-story-warnings-${id}`} className="mt-2 text-sm text-slate-700">{s.warnings}</p>
        <p className="mt-6 font-semibold">References</p>
        <ol data-testid={`stack-story-refs-${id}`} className="mt-2 list-decimal pl-5 space-y-1 text-sm">
          {s.refs.map(([t, u]) => <li key={u}>{t} <a href={u} target="_blank" rel="noreferrer" className="underline decoration-sunny decoration-2 underline-offset-4 break-all">{u}</a></li>)}
        </ol>
        <p className="mt-6 text-sm">{NOT_STUDIED}</p>
        <p data-testid={`stack-story-fda-${id}`} className="mt-2 text-xs italic text-slate-600">*{FDA}</p>
        <h3 className="mt-10 text-2xl">Grandpa's story</h3>
        <p data-testid={`stack-story-text-${id}`} className="mt-3 font-display text-lg leading-relaxed">{s.story}</p>
      </div>
    </section>
  );
};

export const StackFeature = ({ p, i }) => {
  const s = STACKS[p.id];
  const price = usePrice();
  return (
    <div data-testid={`stack-card-${p.id}`} className={`grid gap-8 items-center ${i % 2 ? "md:grid-cols-[1fr_280px] md:[&>*:first-child]:order-2" : "md:grid-cols-[280px_1fr]"}`}>
      {s.ocean ? <OceanImage p={p} className="mx-auto w-full max-w-[280px]" /> : MEDIA[s.reel] && <LazyVideo media={MEDIA[s.reel]} caption={false} testId={`home-stack-reel-${p.id}`} className="mx-auto w-full max-w-[280px]" />}
      <div>
        {s.ocean ? <OceanShip id={`card-${p.id}`} /> : <UsOnly id={`card-${p.id}`} />}
        <h3 className="mt-4 text-2xl md:text-4xl">{p.short_name}</h3>
        {p.subtitle && <p className="mt-1 text-slate-600">{p.subtitle}</p>}
        <ul className="mt-4 space-y-1 text-sm text-slate-700">{p.contents.map((c) => <li key={c} className="flex gap-2"><Check className="h-4 w-4 mt-0.5 shrink-0" strokeWidth={3} />{c}</li>)}</ul>
        {s.ocean
          ? <div className="mt-5"><OceanPrice p={p} testId={`stack-card-price-${p.id}`} /></div>
          : <p data-testid={`stack-card-price-${p.id}`} className="mt-5 font-semibold">{price(p.price_usd)} one-time · {price(subPrice(p))} Subscribe & save</p>}
        <Link to={`/shop/${p.id}`} data-testid={`stack-view-${p.id}`} className="btn-pop bg-sunny text-navy mt-6">View stack <ArrowRight className="h-4 w-4" /></Link>
      </div>
    </div>
  );
};

export const useStacks = () => useStore().products.filter((p) => STACKS[p.id]);
